import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { cafeInfo } from "@/data/menu";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Bitte fülle alle Felder aus.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        message: form.message.trim(),
      });
      setForm({ name: "", email: "", message: "" });
      toast.success("Nachricht gesendet! Wir melden uns in Kürze.");
    } catch (err) {
      console.error(err);
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Bitte überprüfe deine Eingaben (gültige E-Mail nötig)."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="kontakt"
      data-testid="contact-section"
      className="section-pad bg-cream"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-12">
          <div className="md:col-span-5">
            <span className="eyebrow">Kontakt</span>
            <h2 className="font-display mt-4 text-5xl font-bold leading-[0.95] tracking-tight text-brand md:text-6xl">
              Schreib uns. Wir hören zu.
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-[#5c5752]">
              Egal ob Frage, Anregung oder einfach ein Kompliment – wir freuen
              uns auf deine Nachricht.
            </p>

            <div className="mt-10 space-y-4">
              <a
                href={`mailto:${cafeInfo.email}`}
                className="flex items-center gap-3 text-brand link-underline"
                data-testid="contact-info-email"
              >
                <Mail size={18} />
                <span className="font-medium">{cafeInfo.email}</span>
              </a>
              <a
                href={cafeInfo.phoneHref}
                className="flex items-center gap-3 text-brand link-underline"
                data-testid="contact-info-phone"
              >
                <Send size={18} />
                <span className="font-medium">{cafeInfo.phone}</span>
              </a>
            </div>
          </div>

          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="md:col-span-7 grid grid-cols-1 gap-5 rounded-3xl border border-warm bg-white p-8 md:p-10"
          >
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <div>
                <Label
                  htmlFor="contact-name"
                  className="text-xs uppercase tracking-[0.2em] text-[#5c5752]"
                >
                  Name
                </Label>
                <Input
                  id="contact-name"
                  data-testid="contact-input-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Dein Name"
                  className="mt-2 border-warm bg-white"
                />
              </div>
              <div>
                <Label
                  htmlFor="contact-email"
                  className="text-xs uppercase tracking-[0.2em] text-[#5c5752]"
                >
                  E-Mail
                </Label>
                <Input
                  id="contact-email"
                  data-testid="contact-input-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="du@example.com"
                  className="mt-2 border-warm bg-white"
                />
              </div>
            </div>

            <div>
              <Label
                htmlFor="contact-message"
                className="text-xs uppercase tracking-[0.2em] text-[#5c5752]"
              >
                Nachricht
              </Label>
              <Textarea
                id="contact-message"
                data-testid="contact-input-message"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="Worum geht's?"
                className="mt-2 min-h-[140px] border-warm bg-white"
              />
            </div>

            <button
              type="submit"
              data-testid="contact-submit-button"
              disabled={submitting}
              className="btn-primary w-full md:w-auto md:self-start disabled:opacity-60"
            >
              {submitting ? "Wird gesendet…" : "Nachricht senden"}
              <Send size={16} />
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
